import {
  AcademicCapIcon,
  BookOpenIcon,
  BriefcaseIcon,
  CheckBadgeIcon,
  ClipboardDocumentCheckIcon,
  DocumentIcon,
  IdentificationIcon,
  ScaleIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { vaultRoutesBySection } from '~/routing/vaultRoutesConfig';
import { getVaultTopicIcon } from '~/routing/vaultTopicIcons';
import { IconType } from '~/types';

const TAGS = CT.layout.navDrawer;

/**
 * Section icon mapping: section names → representative Heroicon components.
 * Used by the IconRail component to render one icon per section in the
 * collapsed sidebar. Per-page (sub-item) icons come from the single source of
 * truth in vaultTopicIcons.ts (keyed by pageKey) so the sidebar, empty states,
 * and dashboard cards always render the same topic icon (MCV-1495).
 */
export const SECTION_ICON_MAP: Record<string, IconType> = {
  Profile: UserIcon,
  Employment: BriefcaseIcon,
  'Academics and Training': AcademicCapIcon,
  // The Licenses section aggregates Medical License + DEA/CDS. The collapsed
  // rail intentionally shows the section's primary (Medical License) topic icon,
  // which is why this is IdentificationIcon and not the `licenses` topic's
  // ShieldCheckIcon; per-page icons still come from vaultTopicIcons.ts (MCV-1495).
  Licenses: IdentificationIcon,
  Certifications: CheckBadgeIcon,
  CME: BookOpenIcon,
  Privileges: ClipboardDocumentCheckIcon,
  Insurance: ScaleIcon,
  Documents: DocumentIcon,
};

/**
 * Section cyTag mapping for backward compatibility
 * Maps section names to their corresponding Cypress test tags
 */
const SECTION_CYTAGS: Record<string, string> = {
  Profile: TAGS.profileSection || '',
  Employment: TAGS.employmentSection || '',
  'Academics and Training': TAGS.academicsSection || '',
  Licenses: TAGS.licenseSection || '',
  Certifications: TAGS.certificationsSection || '',
  CME: TAGS.cmeSection || '',
  Privileges: TAGS.privilegesSection || '',
  Insurance: TAGS.insuranceSection || '',
  Documents: TAGS.documentSection || '',
};

type SidebarProviderAccountPagesLinksOptions = {
  includeProviderSettings?: boolean;
};

/**
 * Sidebar navigation links for provider account pages.
 *
 * Derived from vaultRoutesConfig as the single source of truth.
 * Automatically updates when vaultRoutesConfig changes.
 */
export const getSidebarProviderAccountPagesLinks = ({
  includeProviderSettings = true,
}: SidebarProviderAccountPagesLinksOptions = {}) =>
  vaultRoutesBySection
    .map(({ section, routes }) => ({
      label: section,
      cyTag: SECTION_CYTAGS[section] || '',
      subMenu: routes
        .filter((route) => includeProviderSettings || route.pageKey !== 'settings')
        .map((route) => ({
          label: route.label,
          href: route.path,
          pageKey: route.pageKey,
          icon: getVaultTopicIcon(route.pageKey),
          cyTag: route.cyTag || '',
          // Preserve tab state properties if they exist in the route config
          ...(route.activeTab && { activeTab: route.activeTab }),
          ...(route.isDefaultTab && { isDefaultTab: route.isDefaultTab }),
          ...(route.tabState !== undefined && { state: route.tabState }),
        })),
    }))
    .filter((section) => section.subMenu.length > 0);

export const SIDEBAR_PROVIDER_ACCOUNT_PAGES_LINKS = getSidebarProviderAccountPagesLinks();
